import React, { useEffect, useRef, useState } from "react";
import { searchAirlines } from "../../lib/formUtils";

/**
 * Multi-tag airline input. User types airline code/name → autocomplete dropdown → select
 * to add a tag. Tags are joined with commas for storage.
 *
 * Props: value (string with "American Airlines, Delta, ..." form), onChange(string), testId
 */
export default function AirlineField({ value, onChange, testId }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const tags = (value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const suggestions = searchAirlines(query || "");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addTag = (airline) => {
    if (!tags.includes(airline.name)) {
      onChange([...tags, airline.name].join(", "));
    }
    setQuery("");
    setOpen(false);
  };

  const removeTag = (tag) => {
    onChange(tags.filter((t) => t !== tag).join(", "));
  };

  return (
    <div ref={ref} className="relative">
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid={testId ? `${testId}-tags` : undefined}>
          {tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-2 px-3 h-9 text-[12px] uppercase tracking-[0.18em] bg-ink text-ivory"
            >
              {t}
              <button
                type="button"
                onClick={() => removeTag(t)}
                className="text-ivory/60 hover:text-gold"
                aria-label={`Remove ${t}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        className="field-editorial"
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === "Tab") && suggestions.length > 0 && query) {
            e.preventDefault();
            addTag(suggestions[0]);
          }
          if (e.key === "Escape") setOpen(false);
        }}
        placeholder="Type airline code or name (e.g., AA, Delta)"
        autoComplete="off"
        data-testid={testId}
      />
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-30 left-0 right-0 mt-1 bg-ivory border border-hairline shadow-lg max-h-72 overflow-y-auto"
          data-testid={testId ? `${testId}-suggestions` : undefined}
        >
          {suggestions.map((a) => (
            <li
              key={a.code}
              onClick={() => addTag(a)}
              className="px-4 py-3 cursor-pointer hover:bg-[#FBF7EC] flex items-baseline justify-between gap-4 border-b border-hairline last:border-0"
            >
              <span className="font-serif text-lg text-ink">{a.code}</span>
              <span className="text-sm text-muted font-light text-right">{a.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
